import { CSSProperties, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Plus } from "lucide-react";
import { createPortal } from "react-dom";

import { QuerySecurity } from "@/api/queries/types";
import { Portfolio } from "@/api/types";

export type BuyOrderRow = {
  id: string;
  securityId: number | null;
  instrumentCode: string;
  instrumentName: string;
  class1code: string;
  class2id: string;
  class2code: string;
  isinCode: string;
  type: "Market" | "Limit" | "";
  limitPrice: string;
  holding: string;
  feePercent: string;
  price: string;
  // Security's last known price at selection time; `price` is user-editable.
  marketUnitPrice?: number;
  marketUnitPriceDate?: string;
  fx: string;
  quantity: string;
  amountInput: string;
  duration: string;
};

type Props = {
  selectedPortfolio?: Portfolio;
  securities: QuerySecurity[];
  initialRows?: BuyOrderRow[];
  onChange?: (rows: BuyOrderRow[]) => void;
};

const inputCls =
  "rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500";

const selectCls =
  "rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 pl-1.5 pr-6 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500";

// --- Instrument autocomplete ---
type ACProps = {
  value: string;
  isSelected: boolean;
  securities: QuerySecurity[];
  onSelect: (
    code: string,
    name: string,
    unitPrice: number,
    securityId: number,
    isinCode: string,
    class2code: string,
    class1code: string
  ) => void;
  onClear: () => void;
};

function InstrumentAutocomplete({
  value,
  isSelected,
  securities,
  onSelect,
  onClear,
}: ACProps) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  // Reposition dropdown on open / scroll / resize
  useEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 2,
      left: rect.left,
      width: 288,
      zIndex: 9999,
    });
  }, [open, query]);

  // When instrument is selected, show name + clear button
  if (isSelected) {
    return (
      <div className="flex items-center gap-1 min-w-0">
        <span className="truncate text-xs font-medium text-gray-800 dark:text-gray-200 max-w-[144px]">
          {value}
        </span>
        <button
          type="button"
          onClick={() => {
            setQuery("");
            onClear();
          }}
          className="shrink-0 text-gray-400 hover:text-red-500 leading-none px-0.5"
          aria-label="Remove instrument"
        >
          ✕
        </button>
      </div>
    );
  }

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = securities
    .filter(
      (s) =>
        !normalizedQuery ||
        s.name?.toLowerCase().includes(normalizedQuery) ||
        s.security_code?.toLowerCase().includes(normalizedQuery) ||
        s.isin_code?.toLowerCase().includes(normalizedQuery)
    )
    .slice(0, 10);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (e.target.value === "") onClear();
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Søk navn / kode"
        className={`${inputCls} w-40`}
      />
      {open &&
        createPortal(
          <ul
            style={dropdownStyle}
            className="max-h-52 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl text-xs"
          >
            {filtered.length > 0 ? (
              filtered.map((s) => (
                <li
                  key={s.id}
                  onMouseDown={() => {
                    setQuery(s.name);
                    onSelect(
                      s.security_code,
                      s.name,
                      s.unitPrice,
                      s.id,
                      s.isin_code ?? "",
                      s.class2code ?? "",
                      s.class1code ?? ""
                    );
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-baseline justify-between gap-2 px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                >
                  <span className="truncate font-medium">{s.name}</span>
                  <span className="shrink-0 font-mono text-gray-400">
                    {s.security_code}
                  </span>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-muted-foreground">
                {securities.length === 0
                  ? "Ingen instrumenter tilgjengelig"
                  : `Ingen treff for «${query}»`}
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
}

// --- Main component ---

const makeEmpty = (): BuyOrderRow => ({
  id: Math.random().toString(36).slice(2),
  securityId: null,
  instrumentCode: "",
  instrumentName: "",
  isinCode: "",
  class1code: "",
  class2id: "",
  class2code: "",
  type: "",
  limitPrice: "",
  holding: "",
  feePercent: "",
  price: "",
  fx: "1",
  quantity: "",
  amountInput: "",
  duration: dayjs().format("YYYY-MM-DD"),
});

export function BuyOrders({
  selectedPortfolio,
  securities,
  initialRows,
  onChange,
}: Props) {
  const [rows, setRows] = useState<BuyOrderRow[]>(() => initialRows ?? []);

  const commit = (next: BuyOrderRow[]) => {
    setRows(next);
    onChange?.(next);
  };

  const addRow = () => commit([...rows, makeEmpty()]);

  const remove = (id: string) => commit(rows.filter((r) => r.id !== id));

  const handleInstrument = (
    id: string,
    code: string,
    name: string,
    unitPrice: number,
    securityId: number,
    isinCode: string,
    class2code: string,
    class1code: string
  ) => {
    const isFund = ["FI", "EF"].includes(class2code);
    const next = rows.map((row) => {
      if (row.id !== id) return row;
      const updated: BuyOrderRow = {
        ...row,
        securityId,
        instrumentCode: code,
        instrumentName: name,
        isinCode,
        class1code,
        class2code,
        type: isFund ? "" : row.type || "Market",
        price: String(unitPrice),
      };
      // auto-fill holding from portfolio
      if (selectedPortfolio) {
        const match =
          selectedPortfolio.portfolioReport.portfolioReportItems.find(
            (item) => item.security.securityCode === code
          );
        updated.holding = match ? String(match.amount) : "";
      }
      // recompute amount if quantity already set
      const qty = parseFloat(updated.quantity) || 0;
      const fx = parseFloat(updated.fx) || 1;
      if (qty > 0 && unitPrice > 0) {
        updated.amountInput = String(Math.round(qty * unitPrice * fx));
      }
      return updated;
    });
    commit(next);
  };

  const handleField = (id: string, field: keyof BuyOrderRow, value: string) => {
    const next = rows.map((row) => {
      if (row.id !== id) return row;
      const updated: BuyOrderRow = { ...row, [field]: value };
      // when switching away from Limit, clear limit-only fields
      if (field === "type" && value !== "Limit") {
        updated.limitPrice = "";
        updated.duration = "";
      }
      const price = parseFloat(field === "price" ? value : updated.price) || 0;
      const fx = parseFloat(field === "fx" ? value : updated.fx) || 1;

      if (field === "quantity") {
        const qty = parseFloat(value) || 0;
        updated.amountInput =
          qty > 0 && price > 0 ? String(Math.round(qty * price * fx)) : "";
      } else if (field === "amountInput") {
        const amt = parseFloat(value) || 0;
        updated.quantity =
          amt > 0 && price > 0 ? String(+(amt / (price * fx)).toFixed(4)) : "";
      } else if (field === "price" || field === "fx") {
        // recompute amount from existing quantity
        const qty = parseFloat(updated.quantity) || 0;
        if (qty > 0 && price > 0) {
          updated.amountInput = String(Math.round(qty * price * fx));
        }
      }
      return updated;
    });
    commit(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-foreground">Kjøpsordre</p>
          <p className="text-[10px] text-muted-foreground">
            Legg til instrumenter og angi kjøpsdetaljer.
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
        >
          <Plus className="size-3.5" />
          Legg til instrument
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
          Ingen kjøpsordre lagt til ennå.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-muted/60 text-muted-foreground">
                {(
                  [
                    { label: "Instrument", align: "left" },
                    { label: "Beholdning", align: "right" },
                    { label: "Gebyr %", align: "left" },
                    { label: "Kurs", align: "right" },
                    { label: "FX", align: "left" },
                    { label: "Antall", align: "right" },
                    { label: "Beløp", align: "right" },
                    { label: "Type", align: "left" },
                    { label: "Limit kurs", align: "right" },
                    { label: "Varighet", align: "left" },
                    { label: "", align: "left" },
                  ] as { label: string; align: "left" | "right" }[]
                ).map((h) => (
                  <th
                    key={h.label}
                    className={`px-1 py-1.5 text-${h.align} font-medium border-b border-gray-200 dark:border-gray-700 whitespace-nowrap`}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-1 py-1">
                    <InstrumentAutocomplete
                      value={row.instrumentName}
                      isSelected={!!row.securityId}
                      securities={securities}
                      onSelect={(
                        code,
                        name,
                        price,
                        securityId,
                        isinCode,
                        class2code,
                        class1code
                      ) =>
                        handleInstrument(
                          row.id,
                          code,
                          name,
                          price,
                          securityId,
                          isinCode,
                          class2code,
                          class1code
                        )
                      }
                      onClear={() => {
                        commit(
                          rows.map((r) =>
                            r.id !== row.id
                              ? r
                              : {
                                  ...r,
                                  securityId: null,
                                  instrumentCode: "",
                                  instrumentName: "",
                                  isinCode: "",
                                  class2id: "",
                                  class2code: "",
                                  type: "",
                                  price: "",
                                  holding: "",
                                  quantity: "",
                                  amountInput: "",
                                }
                          )
                        );
                      }}
                    />
                    {row.isinCode && (
                      <div className="text-[10px] font-mono text-gray-400 mt-0.5 px-0.5">
                        {row.isinCode}
                      </div>
                    )}
                  </td>
                  <td className="px-1 py-1 text-right">
                    <input
                      type="text"
                      value={row.holding}
                      readOnly
                      tabIndex={-1}
                      className="w-16 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-1.5 py-0.5 text-xs text-gray-500 cursor-not-allowed text-right"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      value={row.feePercent}
                      onChange={(e) =>
                        handleField(row.id, "feePercent", e.target.value)
                      }
                      placeholder="0"
                      className={`${inputCls} w-14`}
                    />
                  </td>
                  <td className="px-1 py-1 text-right">
                    <input
                      type="number"
                      value={row.price}
                      onChange={(e) =>
                        handleField(row.id, "price", e.target.value)
                      }
                      placeholder="0"
                      className={`${inputCls} w-20 text-right`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      value={row.fx}
                      onChange={(e) =>
                        handleField(row.id, "fx", e.target.value)
                      }
                      placeholder="1"
                      className={`${inputCls} w-12`}
                    />
                  </td>
                  <td className="px-1 py-1 text-right">
                    <input
                      type="text"
                      value={row.quantity}
                      readOnly
                      tabIndex={-1}
                      className={`${inputCls} w-16 text-right bg-gray-50 dark:bg-gray-900 text-gray-500 cursor-not-allowed`}
                    />
                  </td>
                  <td className="px-1 py-1 text-right">
                    <input
                      type="number"
                      value={row.amountInput}
                      onChange={(e) =>
                        handleField(row.id, "amountInput", e.target.value)
                      }
                      placeholder="0"
                      className={`${inputCls} w-24 text-right`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    {["FI", "EF"].includes(row.class2code) ? (
                      <span className="text-xs text-gray-400 italic px-1">
                        N/A
                      </span>
                    ) : (
                      <select
                        value={row.type}
                        onChange={(e) =>
                          handleField(row.id, "type", e.target.value)
                        }
                        disabled={!row.securityId}
                        className={`${selectCls} disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <option value="" disabled>
                          -- Velg --
                        </option>
                        <option value="Market">Market</option>
                        <option value="Limit">Limit</option>
                      </select>
                    )}
                  </td>
                  <td className="px-1 py-1 text-right">
                    <input
                      type="number"
                      value={row.limitPrice}
                      onChange={(e) =>
                        handleField(row.id, "limitPrice", e.target.value)
                      }
                      placeholder="—"
                      disabled={row.type !== "Limit"}
                      className={`${inputCls} w-20 text-right disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="date"
                      value={row.duration}
                      onChange={(e) =>
                        handleField(row.id, "duration", e.target.value)
                      }
                      disabled={row.type !== "Limit"}
                      className={`${inputCls} w-28 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <button
                      type="button"
                      onClick={() => remove(row.id)}
                      className="text-red-400 hover:text-red-600 px-1 leading-none"
                      aria-label="Remove"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
